import { useState, useCallback, useEffect, useRef } from 'react';
import {
  collection, getDocs, doc, setDoc, updateDoc, increment, addDoc, deleteDoc,
  onSnapshot, query, orderBy, limit, Unsubscribe, where
} from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, fetchSignInMethodsForEmail, signOut, Auth, signInWithEmailAndPassword } from 'firebase/auth';
import { db, APP_ID, API_CONFIG, firebaseConfig } from '../config/firebase';
import { useToast } from '../contexts/ToastContext';
import { formatCurrency } from '../utils/helpers';
import type { Player, Team, Transaction, TransactionType, AppUser, AuctionSettings, PlayerDetails } from '../types';

// Default auction settings
export const DEFAULT_AUCTION_SETTINGS: Omit<AuctionSettings, 'id' | 'updatedAt' | 'updatedBy' | 'updatedByName'> = {
  minBidPoints: 100,
  maxBidPoints: 5000,
  bidIncrement: 50,
  defaultTeamBudget: 10000,
  timerDuration: 15,
  requiredMalePlayers: 0,
  requiredFemalePlayers: 0,
};

interface UseFirestoreDataReturn {
  players: Player[];
  teams: Team[];
  transactions: Transaction[];
  auctionSettings: AuctionSettings | null;
  shortlists: Record<string, string[]>;
  loading: boolean;
  syncing: boolean;
  recalculatingBalances: boolean;
  fetchData: () => Promise<void>;
  syncFromWhatsApp: () => Promise<void>;
  // Team CRUD
  createTeam: (name: string, owner: string, ownerEmail: string, budget: number, currentUser: AppUser) => Promise<boolean>;
  updateTeam: (teamId: string, updates: Partial<Pick<Team, 'name' | 'ownerName' | 'ownerEmail' | 'totalBudget'>>, currentUser: AppUser) => Promise<boolean>;
  deleteTeam: (teamId: string, currentUser: AppUser) => Promise<boolean>;
  recalculateTeamBalances: (currentUser: AppUser) => Promise<void>;
  // Player CRUD
  createPlayer: (playerData: { name: string; mobile_number: string; details: Partial<PlayerDetails> }, currentUser: AppUser) => Promise<boolean>;
  updatePlayer: (playerId: string, updates: { name?: string; details?: Partial<PlayerDetails> }, currentUser: AppUser) => Promise<boolean>;
  deletePlayer: (playerId: string, currentUser: AppUser) => Promise<boolean>;
  // Player operations
  assignPlayerToTeam: (player: Player, teamId: string, price: number, currentUser: AppUser) => Promise<void>;
  removePlayerFromTeam: (player: Player, currentUser: AppUser) => Promise<void>;
  resetAndRefreshPlayers: () => Promise<void>;
  createPlayerUsers: (currentUser: AppUser, onProgress?: (current: number, total: number) => void) => Promise<{
    created: number;
    invalid: number;
    existing: number;
    failed: number;
    total: number;
  }>;
  bulkDeleteUsers: (currentUser: AppUser, roleFilter?: 'player' | 'owner') => Promise<void>;
  fetchShortlists: () => Promise<void>;
  toggleShortlist: (teamId: string, playerId: string, currentUser: AppUser) => Promise<void>;
  // Settings CRUD
  saveAuctionSettings: (settings: Partial<AuctionSettings>, currentUser: AppUser) => Promise<boolean>;
  // Transaction CRUD
  updateTransaction: (transactionId: string, updates: Partial<Transaction>) => Promise<boolean>;
  deleteTransaction: (transactionId: string) => Promise<boolean>;
  // Real-time
  subscribeToRealtime: () => void;
  unsubscribeFromRealtime: () => void;
}

// Helper to log transactions
const logTransaction = async (
  type: TransactionType,
  currentUser: AppUser,
  details: Partial<Transaction>
) => {
  // Strip undefined values because Firestore rejects them
  const cleanDetails = Object.fromEntries(
    Object.entries(details).filter(([, v]) => v !== undefined)
  ) as Partial<Transaction>;

  const transactionData: Omit<Transaction, 'id'> = {
    type,
    timestamp: new Date().toISOString(),
    performedBy: currentUser.uid,
    performedByName: currentUser.name,
    season: '3',
    ...cleanDetails
  };

  await addDoc(
    collection(db, 'artifacts', APP_ID, 'public', 'data', 'transactions'),
    transactionData
  );
};

// --- Auth helper using secondary app to avoid disrupting current session ---
let secondaryAuth: Auth | null = null;
const USERS_COLLECTION_PATH = `artifacts/${APP_ID}/public/data/users`;

const getSecondaryAuth = (): Auth => {
  if (secondaryAuth) return secondaryAuth;
  const existing = getApps().find(app => app.name === 'secondary');
  const app = existing || initializeApp(firebaseConfig, 'secondary');
  secondaryAuth = getAuth(app);
  return secondaryAuth;
};

const ensureAuthUser = async (
  email: string,
  password: string
): Promise<{ status: 'created' | 'exists' | 'error'; uid?: string; error?: string }> => {
  try {
    const authInstance = getSecondaryAuth();
    const methods = await fetchSignInMethodsForEmail(authInstance, email);
    if (methods.length > 0) {
      return { status: 'exists' };
    }
    const cred = await createUserWithEmailAndPassword(authInstance, email, password);
    try { await signOut(authInstance); } catch {}
    return { status: 'created', uid: cred.user.uid };
  } catch (err) {
    console.error('ensureAuthUser error', err);
    const message = err instanceof Error ? err.message : 'Unknown error';
    return { status: 'error', error: message };
  }
};

export const useFirestoreData = (): UseFirestoreDataReturn => {
  const toast = useToast();
  const [players, setPlayers] = useState<Player[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [auctionSettings, setAuctionSettings] = useState<AuctionSettings | null>(null);
  const [shortlists, setShortlists] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [recalculatingBalances, setRecalculatingBalances] = useState(false);

  // Store unsubscribe functions
  const unsubscribeRefs = useRef<Unsubscribe[]>([]);

  // Sort players helper - sort by createdDatetime (newest first)
  const sortPlayers = (playersList: Player[]): Player[] => {
    return [...playersList].sort((a, b) => {
      // Primary: Sort by createdDatetime (newest first)
      const dateA = a.createdDatetime ? new Date(a.createdDatetime).getTime() : 0;
      const dateB = b.createdDatetime ? new Date(b.createdDatetime).getTime() : 0;
      if (dateA !== dateB) return dateB - dateA; // Descending (newest first)
      // Fallback: Sort by name
      return a.name.localeCompare(b.name);
    });
  };

  // Subscribe to real-time updates
  const subscribeToRealtime = useCallback(() => {
    // Unsubscribe from any existing subscriptions
    unsubscribeRefs.current.forEach(unsub => unsub());
    unsubscribeRefs.current = [];

    // Subscribe to teams
    const teamsQuery = collection(db, 'artifacts', APP_ID, 'public', 'data', 'teams');
    const unsubTeams = onSnapshot(teamsQuery, (snapshot) => {
      const teamsList = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Team[];
      setTeams(teamsList);
    }, (error) => {
      console.error('Teams realtime error:', error);
    });
    unsubscribeRefs.current.push(unsubTeams);

    // Subscribe to players
    const playersQuery = collection(db, 'artifacts', APP_ID, 'public', 'data', 'players');
    const unsubPlayers = onSnapshot(playersQuery, (snapshot) => {
      const playersList = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Player[];
      setPlayers(sortPlayers(playersList));
    }, (error) => {
      console.error('Players realtime error:', error);
    });
    unsubscribeRefs.current.push(unsubPlayers);

    // Subscribe to transactions (last 100)
    const transactionsQuery = query(
      collection(db, 'artifacts', APP_ID, 'public', 'data', 'transactions'),
      orderBy('timestamp', 'desc'),
      limit(100)
    );
    const unsubTransactions = onSnapshot(transactionsQuery, (snapshot) => {
      const txList = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Transaction[];
      setTransactions(txList);
    }, (error) => {
      console.error('Transactions realtime error:', error);
    });
    unsubscribeRefs.current.push(unsubTransactions);

    // Subscribe to auction settings
    const settingsRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'auction');
    const unsubSettings = onSnapshot(settingsRef, (snapshot) => {
      if (snapshot.exists()) {
        setAuctionSettings({ id: snapshot.id, ...snapshot.data() } as AuctionSettings);
      } else {
        // No settings yet - use defaults
        setAuctionSettings(null);
      }
    }, (error) => {
      console.error('Settings realtime error:', error);
    });
    unsubscribeRefs.current.push(unsubSettings);
  }, []);

  // Unsubscribe from real-time updates
  const unsubscribeFromRealtime = useCallback(() => {
    unsubscribeRefs.current.forEach(unsub => unsub());
    unsubscribeRefs.current = [];
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeFromRealtime();
    };
  }, [unsubscribeFromRealtime]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const teamsSnap = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'teams'));
      const teamsList = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() })) as Team[];
      setTeams(teamsList);

      const playersSnap = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'players'));
      const playersList = playersSnap.docs.map(d => ({ id: d.id, ...d.data() })) as Player[];
      setPlayers(sortPlayers(playersList));

      // Also fetch recent transactions
      const txQuery = query(
        collection(db, 'artifacts', APP_ID, 'public', 'data', 'transactions'),
        orderBy('timestamp', 'desc'),
        limit(100)
      );
      const txSnap = await getDocs(txQuery);
      const txList = txSnap.docs.map(d => ({ id: d.id, ...d.data() })) as Transaction[];
      setTransactions(txList);

      // Fetch shortlists
      const shortlistSnap = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'shortlists'));
      const shortlistMap: Record<string, string[]> = {};
      shortlistSnap.forEach(docSnap => {
        const data = docSnap.data() as { playerIds?: string[] };
        shortlistMap[docSnap.id] = data.playerIds || [];
      });
      setShortlists(shortlistMap);
    } catch (err) {
      console.error("Firestore Error:", err);
      toast.error("Failed to load database. Check your Firestore Security Rules.");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  const syncFromWhatsApp = useCallback(async () => {
    setSyncing(true);
    try {
      const response = await fetch(API_CONFIG.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Auth-Token': API_CONFIG.token }
      });
      
      const result = await response.json();
      if (!result.success) throw new Error("API Failed");

      let newCount = 0;
      let updatedCount = 0;

      for (const item of result.data) {
        const existingPlayer = players.find(p => p.mobile_number === item.mobile_number);

        // Parse response_json to get details
        let details: Record<string, unknown> = {};
        try {
          details = JSON.parse(item.response_json || '{}');
        } catch(e) {
          console.warn('Failed to parse response_json for', item.name);
        }

        if (!existingPlayer) {
          // New player - add to Firestore
          const playerData = {
            ...item,
            name: (details.player_full_name as string) || item.name,
            details: details,
            status: 'unsold' as const,
            teamId: null,
            soldPrice: 0,
            season: '3',
            registeredAt: new Date().toISOString(),
            createdDatetime: item.createdDatetime || null  // UTC datetime from API
          };

          await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', item.id), playerData);
          newCount++;
        } else if (!existingPlayer.manuallyEdited && (!existingPlayer.details || Object.keys(existingPlayer.details).length === 0 || !existingPlayer.createdDatetime)) {
          // Existing player without details or createdDatetime - update ONLY if NOT manually edited
          await updateDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', existingPlayer.id), {
            details: details,
            name: (details.player_full_name as string) || existingPlayer.name,
            createdDatetime: item.createdDatetime || existingPlayer.createdDatetime || null
          });
          updatedCount++;
        }
        // Skip players that have been manually edited - preserve their changes
      }

      let message = `Sync Complete! Added ${newCount} new players.`;
      if (updatedCount > 0) {
        message += ` Updated details for ${updatedCount} existing players.`;
      }
      toast.success(message);
      await fetchData();
    } catch (err) {
      toast.error("Sync Failed: " + (err as Error).message);
    } finally {
      setSyncing(false);
    }
  }, [players, fetchData, toast]);

  // Reset and refresh all players from WhatsApp API (deletes existing unsold players and re-syncs)
  const resetAndRefreshPlayers = useCallback(async () => {
    setSyncing(true);
    try {
      // Step 1: Delete all UNSOLD players from Firestore (keep sold players intact)
      const playersSnap = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'players'));
      let deleteCount = 0;

      for (const docSnap of playersSnap.docs) {
        const player = docSnap.data() as Player;
        if (player.status !== 'sold') {
          await deleteDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', docSnap.id));
          deleteCount++;
        }
      }

      toast.info(`Deleted ${deleteCount} unsold players. Fetching fresh data...`);

      // Step 2: Fetch fresh data from WhatsApp API
      const response = await fetch(API_CONFIG.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Auth-Token': API_CONFIG.token }
      });

      const result = await response.json();
      if (!result.success) throw new Error("API Failed");

      // Step 3: Get current sold players (to skip them)
      const soldPlayersSnap = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'players'));
      const soldMobiles = new Set(soldPlayersSnap.docs.map(d => (d.data() as Player).mobile_number));

      // Step 4: Add all players from API (skip already sold ones)
      let addedCount = 0;
      for (const item of result.data) {
        if (soldMobiles.has(item.mobile_number)) continue; // Skip sold players

        let details: Record<string, unknown> = {};
        try {
          details = JSON.parse(item.response_json || '{}');
        } catch(e) {
          console.warn('Failed to parse response_json for', item.name);
        }

        const playerData = {
          ...item,
          name: (details.player_full_name as string) || item.name,
          details: details,
          status: 'unsold' as const,
          teamId: null,
          soldPrice: 0,
          season: '3',
          registeredAt: new Date().toISOString(),
          createdDatetime: item.createdDatetime || null  // UTC datetime from API
        };

        await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', item.id), playerData);
        addedCount++;
      }

      toast.success(`Reset Complete! Deleted ${deleteCount} old records, added ${addedCount} fresh players.`);
      await fetchData();
    } catch (err) {
      toast.error("Reset Failed: " + (err as Error).message);
    } finally {
      setSyncing(false);
    }
  }, [fetchData, toast]);

  const createTeam = useCallback(async (
    name: string,
    owner: string,
    ownerEmail: string,
    budget: number,
    currentUser: AppUser
  ): Promise<boolean> => {
    if (!name || !owner || !ownerEmail) {
      toast.error("Please provide team name, owner name, and owner email.");
      return false;
    }
    try {
      const teamData = {
        name,
        ownerName: owner,
        ownerEmail: ownerEmail || null,
        totalBudget: budget,
        currentBalance: budget,
        season: '3',
        createdAt: new Date().toISOString()
      };
      const docRef = await addDoc(collection(db, 'artifacts', APP_ID, 'public', 'data', 'teams'), teamData);

      // Log transaction
      await logTransaction('TEAM_CREATED', currentUser, {
        teamId: docRef.id,
        teamName: name,
        amount: budget,
        notes: `Team created with ${formatCurrency(budget)} pts budget. Owner: ${owner}`
      });

      // Auto-create owner user if not present
      const email = ownerEmail;
      const password = ownerEmail; // per requirement
      const existingUserSnap = await getDocs(query(collection(db, USERS_COLLECTION_PATH), where('email', '==', email)));
      if (existingUserSnap.empty) {
        const result = await ensureAuthUser(email, password);
        if (result.status === 'created' && result.uid) {
          await setDoc(doc(db, USERS_COLLECTION_PATH, result.uid), {
            email,
            name: owner,
            role: 'owner',
            teamId: docRef.id,
            createdAt: new Date().toISOString(),
            createdBy: currentUser.uid
          });
          toast.success(`Login created for ${owner} (${email})`);
          await logTransaction('USER_CREATED', currentUser, {
            userEmail: email,
            userName: owner,
            userRole: 'owner',
            teamId: docRef.id
          });
        } else if (result.status === 'exists') {
          toast.warning(`Login already exists for ${email}. Please link manually if needed.`);
        } else {
          toast.warning(`Could not create login for ${owner}. ${result.error || ''}`);
        }
      }

      toast.success(`Team "${name}" created successfully!`);
      await fetchData();
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to create team.");
      return false;
    }
  }, [fetchData, toast]);

  const assignPlayerToTeam = useCallback(async (
    player: Player,
    teamId: string,
    price: number,
    currentUser: AppUser
  ) => {
    const team = teams.find(t => t.id === teamId);
    if (!team) return;
    if (team.currentBalance < price) {
      toast.error("Insufficient Wallet Balance!");
      return;
    }

    // Check squad limits
    if (auctionSettings) {
      const maxMale = auctionSettings.requiredMalePlayers || 0;
      const maxFemale = auctionSettings.requiredFemalePlayers || 0;
      const gender = player.details?.player_gender?.toLowerCase() || '';

      if (maxMale > 0 && gender === 'male') {
        const maleCount = players.filter(p => p.teamId === teamId && p.details?.player_gender?.toLowerCase() === 'male').length;
        if (maleCount >= maxMale) {
          toast.error(`Limit reached: Maximum ${maxMale} male players allowed.`);
          return;
        }
      }

      if (maxFemale > 0 && gender === 'female') {
        const femaleCount = players.filter(p => p.teamId === teamId && p.details?.player_gender?.toLowerCase() === 'female').length;
        if (femaleCount >= maxFemale) {
          toast.error(`Limit reached: Maximum ${maxFemale} female players allowed.`);
          return;
        }
      }
    }

    const previousBalance = team.currentBalance;
    const newBalance = previousBalance - price;

    try {
      const teamRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'teams', teamId);
      await updateDoc(teamRef, { currentBalance: increment(-price) });

      const playerRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', player.id);
      await updateDoc(playerRef, { status: 'sold', teamId, soldPrice: price });

      // Log transaction
      await logTransaction('PLAYER_SOLD', currentUser, {
        playerId: player.id,
        playerName: player.name,
        teamId: teamId,
        teamName: team.name,
        amount: price,
        previousBalance,
        newBalance,
        notes: `${player.name} sold to ${team.name} for ${formatCurrency(price)} pts`
      });

      toast.success(`${player.name} sold to ${team.name} for ${formatCurrency(price)} pts!`);
      // Data will update via real-time listener, but fetch as fallback
      if (unsubscribeRefs.current.length === 0) {
        await fetchData();
      }
    } catch (err) {
      toast.error("Transaction failed: " + (err as Error).message);
    }
  }, [teams, fetchData, toast, auctionSettings, players]);

  const removePlayerFromTeam = useCallback(async (player: Player, currentUser: AppUser) => {
    if (player.status !== 'sold' || !player.teamId) return;

    const team = teams.find(t => t.id === player.teamId);
    const previousBalance = team?.currentBalance || 0;
    const newBalance = previousBalance + player.soldPrice;

    try {
      const teamRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'teams', player.teamId);
      await updateDoc(teamRef, { currentBalance: increment(player.soldPrice) });

      const playerRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', player.id);
      await updateDoc(playerRef, { status: 'unsold', teamId: null, soldPrice: 0 });

      // Log transaction
      await logTransaction('PLAYER_REMOVED', currentUser, {
        playerId: player.id,
        playerName: player.name,
        teamId: player.teamId,
        teamName: team?.name,
        amount: player.soldPrice,
        previousBalance,
        newBalance,
        notes: `${player.name} removed from ${team?.name}. ${formatCurrency(player.soldPrice)} pts refunded.`
      });

      toast.success(`${player.name} removed. ${formatCurrency(player.soldPrice)} pts refunded!`);
      // Data will update via real-time listener, but fetch as fallback
      if (unsubscribeRefs.current.length === 0) {
        await fetchData();
      }
    } catch (err) {
      toast.error("Refund failed: " + (err as Error).message);
    }
  }, [teams, fetchData, toast]);

  // Bulk create player users based on mobile number
  const createPlayerUsers = useCallback(async (currentUser: AppUser, onProgress?: (current: number, total: number) => void) => {
    if (!['admin', 'sub_admin'].includes(currentUser.role)) {
      toast.error('Only admins can create player logins');
      return { created: 0, invalid: 0, existing: 0, failed: 0, total: players.length };
    }

    const existingUsersSnap = await getDocs(collection(db, USERS_COLLECTION_PATH));
    const existingEmails = new Set<string>();
    existingUsersSnap.forEach(docSnap => {
      const data = docSnap.data() as { email?: string };
      if (data.email) existingEmails.add(data.email.toLowerCase());
    });

    const totalCandidates = players.length;
    let processed = 0;
    let createdCount = 0;
    let skippedInvalidMobile = 0;
    let skippedExisting = 0;
    let failedCount = 0;
    const failedSamples: string[] = [];
    const invalidSamples: string[] = [];
    const existingSamples: string[] = [];
    let loggedFirstInvalid = false;

    for (const player of players) {
      // Normalize mobile: take last 10 digits
      const digits = (player.mobile_number || '').replace(/\D/g, '');
      const mobile = digits.slice(-10);
      if (!/^\d{10}$/.test(mobile)) {
        skippedInvalidMobile++;
        if (invalidSamples.length < 3) invalidSamples.push(player.name);
        if (!loggedFirstInvalid) {
          console.warn('Player login skip (invalid mobile):', {
            playerId: player.id,
            name: player.name,
            mobile: player.mobile_number,
            normalized: mobile
          });
          loggedFirstInvalid = true;
        }
        continue;
      }
      processed++;
      if (onProgress) onProgress(processed, totalCandidates);
      const email = `${mobile}@ryametro.com`.toLowerCase();
      if (existingEmails.has(email)) {
        skippedExisting++;
        if (existingSamples.length < 3) existingSamples.push(player.name);
        continue;
      }
      const result = await ensureAuthUser(email, mobile);
      if (result.status === 'exists') {
        // Try to sign in with expected password to fetch UID and create Firestore doc if missing
        try {
          const authInstance = getSecondaryAuth();
          const cred = await signInWithEmailAndPassword(authInstance, email, mobile);
          const uid = cred.user.uid;
          if (!existingEmails.has(email)) {
            await setDoc(doc(db, USERS_COLLECTION_PATH, uid), {
              email,
              name: player.name,
              role: 'player',
              teamId: player.teamId || null,
              playerId: player.id,
              mobile,
              createdAt: new Date().toISOString(),
              createdBy: currentUser.uid
            });
            existingEmails.add(email);
            createdCount++;
            await logTransaction('USER_CREATED', currentUser, {
              userEmail: email,
              userName: player.name,
              userRole: 'player',
              playerId: player.id,
              teamId: player.teamId || undefined
            });
          } else {
            skippedExisting++;
            if (existingSamples.length < 3) existingSamples.push(player.name);
          }
          try { await signOut(authInstance); } catch {}
        } catch {
          skippedExisting++;
          if (existingSamples.length < 3) existingSamples.push(player.name);
        }
        continue;
      }
      if (result.status === 'error' || !result.uid) {
        failedCount++;
        if (failedSamples.length < 3) failedSamples.push(`${player.name} (${result.error || 'unknown'})`);
        continue;
      }
      await setDoc(doc(db, USERS_COLLECTION_PATH, result.uid), {
        email,
        name: player.name,
        role: 'player',
        teamId: player.teamId || null,
        playerId: player.id,
        mobile,
        createdAt: new Date().toISOString(),
        createdBy: currentUser.uid
      });
      existingEmails.add(email);
      createdCount++;
      await logTransaction('USER_CREATED', currentUser, {
        userEmail: email,
        userName: player.name,
        userRole: 'player',
        playerId: player.id,
        teamId: player.teamId || undefined
      });
    }

    const parts = [`Created: ${createdCount}`];
    if (skippedInvalidMobile) parts.push(`Invalid mobile: ${skippedInvalidMobile}`);
    if (skippedExisting) parts.push(`Already existed: ${skippedExisting}`);
    if (failedCount) parts.push(`Failed: ${failedCount}`);

    toast.success(`Player logins processed. ${parts.join(' | ')}`);

    if (invalidSamples.length) {
      toast.info(`Invalid mobile skipped: ${invalidSamples.join(', ')}`);
    }
    if (failedSamples.length) {
      toast.error(`Failed to create: ${failedSamples.join(', ')}`);
    }

    return {
      created: createdCount,
      invalid: skippedInvalidMobile,
      existing: skippedExisting,
      failed: failedCount,
      total: totalCandidates
    };
  }, [players, toast]);

  // Bulk delete users by role (player/owner) while keeping admins/sub-admins safe
  const bulkDeleteUsers = useCallback(async (currentUser: AppUser, roleFilter: 'player' | 'owner' = 'player') => {
    if (currentUser.role !== 'admin') {
      toast.error('Only admins can delete users');
      return;
    }

    const usersSnap = await getDocs(collection(db, USERS_COLLECTION_PATH));
    let deleteCount = 0;
    for (const docSnap of usersSnap.docs) {
      const data = docSnap.data() as { role?: string };
      if (data.role === roleFilter) {
        await deleteDoc(docSnap.ref);
        deleteCount++;
      }
    }

    await logTransaction('USER_DELETED', currentUser, {
      notes: `Bulk delete: removed ${deleteCount} ${roleFilter} users`,
      userRole: roleFilter
    });

    toast.success(`Deleted ${deleteCount} ${roleFilter} users`);
  }, [toast]);

  // Shortlists: fetch all
  const fetchShortlists = useCallback(async () => {
    const shortlistSnap = await getDocs(collection(db, 'artifacts', APP_ID, 'public', 'data', 'shortlists'));
    const shortlistMap: Record<string, string[]> = {};
    shortlistSnap.forEach(docSnap => {
      const data = docSnap.data() as { playerIds?: string[] };
      shortlistMap[docSnap.id] = data.playerIds || [];
    });
    setShortlists(shortlistMap);
  }, []);

  // Toggle shortlist for a team
  const toggleShortlist = useCallback(async (teamId: string, playerId: string, currentUser: AppUser) => {
    const docRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'shortlists', teamId);
    const currentList = shortlists[teamId] || [];
    const updated = currentList.includes(playerId)
      ? currentList.filter(id => id !== playerId)
      : [...currentList, playerId];
    await setDoc(docRef, {
      playerIds: updated,
      updatedAt: new Date().toISOString(),
      updatedBy: currentUser.uid,
      updatedByName: currentUser.name
    });
    setShortlists(prev => ({ ...prev, [teamId]: updated }));
  }, [shortlists]);

  // Update an existing team
  const updateTeam = useCallback(async (
    teamId: string,
    updates: Partial<Pick<Team, 'name' | 'ownerName' | 'ownerEmail' | 'totalBudget'>>,
    currentUser: AppUser
  ): Promise<boolean> => {
    const team = teams.find(t => t.id === teamId);
    if (!team) {
      toast.error("Team not found.");
      return false;
    }

    try {
      const teamRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'teams', teamId);

      // If budget is being changed, adjust currentBalance proportionally
      const updateData: Record<string, any> = { ...updates };
      if (updates.totalBudget !== undefined && updates.totalBudget !== team.totalBudget) {
        const budgetDiff = updates.totalBudget - team.totalBudget;
        updateData.currentBalance = team.currentBalance + budgetDiff;

        // Log budget adjustment
        await logTransaction('BUDGET_ADJUSTED', currentUser, {
          teamId,
          teamName: updates.name || team.name,
          amount: budgetDiff,
          previousBalance: team.currentBalance,
          newBalance: team.currentBalance + budgetDiff,
          notes: `Budget adjusted by ${budgetDiff > 0 ? '+' : '-'}${formatCurrency(Math.abs(budgetDiff))} pts`
        });
      }

      await updateDoc(teamRef, updateData);
      toast.success(`Team "${updates.name || team.name}" updated successfully!`);

      if (unsubscribeRefs.current.length === 0) {
        await fetchData();
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to update team.");
      return false;
    }
  }, [teams, fetchData, toast]);

  // Delete a team
  const deleteTeam = useCallback(async (teamId: string, currentUser: AppUser): Promise<boolean> => {
    const team = teams.find(t => t.id === teamId);
    if (!team) {
      toast.error("Team not found.");
      return false;
    }

    // Check if team has players
    const teamPlayers = players.filter(p => p.teamId === teamId);
    if (teamPlayers.length > 0) {
      toast.error(`Cannot delete team with ${teamPlayers.length} players. Remove players first.`);
      return false;
    }

    try {
      const teamRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'teams', teamId);
      await deleteDoc(teamRef);

      // Log the deletion (as a budget adjustment to 0)
      await logTransaction('BUDGET_ADJUSTED', currentUser, {
        teamId,
        teamName: team.name,
        amount: -team.totalBudget,
        notes: `Team "${team.name}" deleted by ${currentUser.name}`
      });

      toast.success(`Team "${team.name}" deleted successfully!`);

      if (unsubscribeRefs.current.length === 0) {
        await fetchData();
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete team.");
      return false;
    }
  }, [teams, players, fetchData, toast]);

  const recalculateTeamBalances = useCallback(async (currentUser: AppUser) => {
    if (!['admin', 'sub_admin'].includes(currentUser.role)) {
      toast.error('Only admins can recalculate balances.');
      return;
    }

    setRecalculatingBalances(true);

    try {
      const balanceUpdates = teams.map(team => {
        const spent = players
          .filter(p => p.teamId === team.id && p.status === 'sold')
          .reduce((sum, p) => sum + (p.soldPrice || 0), 0);
        const recalculatedBalance = Math.max(team.totalBudget - spent, 0);

        return {
          team,
          recalculatedBalance
        };
      });

      const changes = balanceUpdates.filter(({ team, recalculatedBalance }) => team.currentBalance !== recalculatedBalance);

      if (changes.length === 0) {
        toast.success('Balances already up to date.');
        return;
      }

      await Promise.all(changes.map(async ({ team, recalculatedBalance }) => {
        const teamRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'teams', team.id);
        await updateDoc(teamRef, { currentBalance: recalculatedBalance });
      }));

      toast.success(`Recalculated balances for ${changes.length} team${changes.length === 1 ? '' : 's'}.`);
    } catch (err) {
      console.error('Failed to recalculate balances', err);
      toast.error('Failed to recalculate balances.');
    } finally {
      setRecalculatingBalances(false);
    }
  }, [players, teams, toast]);

  // --- Player CRUD ---

  // Create a new player manually
  const createPlayer = useCallback(async (
    playerData: { name: string; mobile_number: string; details: Partial<PlayerDetails> },
    currentUser: AppUser
  ): Promise<boolean> => {
    // Check for duplicate mobile number
    if (players.some(p => p.mobile_number === playerData.mobile_number)) {
      toast.error("A player with this mobile number already exists.");
      return false;
    }

    try {
      const newId = `manual_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const newPlayer: Player = {
        id: newId,
        name: playerData.name,
        mobile_number: playerData.mobile_number,
        status: 'unsold',
        teamId: null,
        soldPrice: 0,
        season: '3',
        details: {
          player_full_name: playerData.name,
          ...playerData.details
        },
        registeredAt: new Date().toISOString(),
        manuallyEdited: true,
        createdBy: currentUser.uid,
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: currentUser.uid
      };

      await setDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', newId), newPlayer);
      toast.success(`Player "${playerData.name}" created successfully!`);

      if (unsubscribeRefs.current.length === 0) {
        await fetchData();
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to create player.");
      return false;
    }
  }, [players, fetchData, toast]);

  // Update an existing player
  const updatePlayer = useCallback(async (
    playerId: string,
    updates: { name?: string; details?: Partial<PlayerDetails> },
    currentUser: AppUser
  ): Promise<boolean> => {
    const player = players.find(p => p.id === playerId);
    if (!player) {
      toast.error("Player not found.");
      return false;
    }

    try {
      const playerRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', playerId);

      const updateData: Record<string, any> = {
        manuallyEdited: true,
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: currentUser.uid
      };

      if (updates.name) {
        updateData.name = updates.name;
      }

      if (updates.details) {
        // Merge with existing details
        updateData.details = {
          ...player.details,
          ...updates.details,
          player_full_name: updates.name || updates.details.player_full_name || player.details?.player_full_name
        };
      }

      await updateDoc(playerRef, updateData);
      toast.success(`Player "${updates.name || player.name}" updated successfully!`);

      if (unsubscribeRefs.current.length === 0) {
        await fetchData();
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to update player.");
      return false;
    }
  }, [players, fetchData, toast]);

  // Delete a player
  const deletePlayer = useCallback(async (
    playerId: string,
    currentUser: AppUser
  ): Promise<boolean> => {
    const player = players.find(p => p.id === playerId);
    if (!player) {
      toast.error("Player not found.");
      return false;
    }

    // Prevent deleting sold players
    if (player.status === 'sold') {
      toast.error("Cannot delete a sold player. Remove from team first.");
      return false;
    }

    try {
      await deleteDoc(doc(db, 'artifacts', APP_ID, 'public', 'data', 'players', playerId));
      toast.success(`Player "${player.name}" deleted successfully!`);

      // Log deletion
      await logTransaction('PLAYER_REMOVED', currentUser, {
        playerId: player.id,
        playerName: player.name,
        notes: `Player "${player.name}" deleted by ${currentUser.name}`
      });

      if (unsubscribeRefs.current.length === 0) {
        await fetchData();
      }
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete player.");
      return false;
    }
  }, [players, fetchData, toast]);

  // Save auction settings
  const saveAuctionSettings = useCallback(async (
    settings: Partial<AuctionSettings>,
    currentUser: AppUser
  ): Promise<boolean> => {
    try {
      const settingsRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'settings', 'auction');

      const settingsData = {
        ...DEFAULT_AUCTION_SETTINGS,
        ...auctionSettings,
        ...settings,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
        updatedByName: currentUser.name,
      };

      // Remove id before saving (it's the doc ID)
      const { id: _id, ...dataToSave } = settingsData as AuctionSettings;

      await setDoc(settingsRef, dataToSave);
      toast.success("Auction settings saved successfully!");
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to save settings.");
      return false;
    }
  }, [auctionSettings, toast]);

  // Update a transaction
  const updateTransaction = useCallback(async (
    transactionId: string,
    updates: Partial<Transaction>
  ): Promise<boolean> => {
    try {
      const txRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'transactions', transactionId);
      // Remove id from updates if present
      const { id, ...updateData } = updates as Transaction;
      await updateDoc(txRef, updateData);
      toast.success("Transaction updated successfully!");
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to update transaction.");
      return false;
    }
  }, [toast]);

  // Delete a transaction
  const deleteTransaction = useCallback(async (transactionId: string): Promise<boolean> => {
    try {
      const txRef = doc(db, 'artifacts', APP_ID, 'public', 'data', 'transactions', transactionId);
      await deleteDoc(txRef);
      toast.success("Transaction deleted successfully!");
      return true;
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete transaction.");
      return false;
    }
  }, [toast]);

  return {
    players, teams, transactions, auctionSettings, shortlists, loading, syncing, recalculatingBalances,
    fetchData, syncFromWhatsApp, resetAndRefreshPlayers,
    createTeam, updateTeam, deleteTeam, recalculateTeamBalances,
    createPlayer, updatePlayer, deletePlayer,
    assignPlayerToTeam, removePlayerFromTeam,
    createPlayerUsers,
    bulkDeleteUsers,
    fetchShortlists,
    toggleShortlist,
    saveAuctionSettings,
    updateTransaction, deleteTransaction,
    subscribeToRealtime, unsubscribeFromRealtime
  };
};
